/**
 * usePicksHealthCheck - Monitors API health and polls for recovery when services are down.
 */

import { API_ENDPOINTS, apiUrl } from '@config/api';
import {
  ALL_DOWN,
  ASSUMED_HEALTHY,
  type ExternalApiHealth,
  readExternalApis,
} from '@parlay-picker/shared/serviceHealth';
import { safeParseJson } from '@utils/api';
import { useEffect, useRef, useState } from 'react';

// Health check polling interval (5 minutes)
const HEALTH_POLL_INTERVAL_MS = 5 * 60 * 1000;

export interface UsePicksHealthCheckResult {
  apiHealth: ExternalApiHealth;
  setApiHealth: React.Dispatch<React.SetStateAction<ExternalApiHealth>>;
  checkApiHealth: () => Promise<void>;
  startHealthPolling: () => void;
}

export function usePicksHealthCheck(): UsePicksHealthCheckResult {
  const [apiHealth, setApiHealth] = useState<ExternalApiHealth>(ASSUMED_HEALTHY);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const checkApiHealth = async (): Promise<void> => {
    try {
      const res = await fetch(apiUrl(API_ENDPOINTS.HEALTH));
      const health = readExternalApis(await safeParseJson(res));
      if (!health) throw new Error('Unexpected health response');
      setApiHealth(health);
      // If Odds API is back up, stop polling
      if (health.oddsApi && intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    } catch {
      setApiHealth(ALL_DOWN);
    }
  };

  const startHealthPolling = (): void => {
    if (!intervalRef.current) {
      // Check immediately, then poll at configured interval
      checkApiHealth();
      intervalRef.current = setInterval(checkApiHealth, HEALTH_POLL_INTERVAL_MS);
    }
  };

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, []);

  return { apiHealth, setApiHealth, checkApiHealth, startHealthPolling };
}
